import { formatDateShort, plural, telHref, type DueInspection } from '@kominiarz/shared';
import { CalendarCheck, CalendarPlus, History, MapPin, Phone, PhoneCall } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Link } from 'react-router';
import { Alert } from '../../components/Alert';
import { Badge } from '../../components/Badge';
import { ButtonAnchor, ButtonLink } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ChoiceGroup } from '../../components/form/ChoiceGroup';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../lib/api';
import { todayLocal } from '../../lib/calendarDates';
import { useDueInspections } from '../../lib/closingApi';
import { readPref, writePref } from '../../lib/storage';
import styles from './closing.module.css';

const RANGES = ['30', '90'] as const;
type Range = (typeof RANGES)[number];
const RANGE_KEY = 'due.days';

/**
 * Addresses whose periodic inspection is due soon (or overdue) and that have no
 * visit planned yet: the "call and arrange" list.
 */
export function DuePage() {
  const [range, setRange] = useState<Range>(() => {
    const stored = readPref(RANGE_KEY);
    return RANGES.includes(stored as Range) ? (stored as Range) : '30';
  });
  const query = useDueInspections(Number(range));
  const items = query.data?.items ?? [];

  return (
    <>
      <PageHeader title="Do umówienia" subtitle="Adresy, którym zbliża się termin przeglądu" />
      <PageBody>
        <div className={styles.filter}>
          <ChoiceGroup<Range>
            legend="Termin przeglądu"
            value={range}
            columns={2}
            options={[
              { value: '30', label: 'Do 30 dni' },
              { value: '90', label: 'Do 90 dni' },
            ]}
            onChange={(r) => {
              setRange(r);
              writePref(RANGE_KEY, r);
            }}
          />
        </div>
        {query.isPending && <Spinner />}
        {query.isError && <Alert kind="error">{errorMessage(query.error)}</Alert>}
        {query.data && items.length === 0 && (
          <EmptyState icon={PhoneCall} title="Nikogo nie trzeba teraz umawiać">
            Gdy po wykonanej wizycie zapiszesz termin kolejnego przeglądu, adres pojawi się tutaj na {range} dni przed
            terminem.
          </EmptyState>
        )}
        {items.length > 0 && (
          <ul className={styles.list}>
            {items.map((item) => (
              <DueItem key={item.objectId} item={item} />
            ))}
          </ul>
        )}
      </PageBody>
    </>
  );
}

function dueLabel(due: string): { text: string; overdue: boolean } {
  const days = Math.round(DateTime.fromISO(due).diff(DateTime.fromISO(todayLocal()), 'days').days);
  if (days < 0) return { text: `Po terminie ${plural(-days, 'dzień', 'dni', 'dni')}`, overdue: true };
  if (days === 0) return { text: 'Termin dzisiaj', overdue: false };
  return { text: `Za ${plural(days, 'dzień', 'dni', 'dni')}`, overdue: false };
}

function DueItem({ item }: { item: DueInspection }) {
  const label = dueLabel(item.due);
  const phone = item.client.phones[0];
  const params = new URLSearchParams({ klient: String(item.client.id), obiekt: String(item.objectId) });
  if (item.serviceType) params.set('usluga', String(item.serviceType.id));

  return (
    <li className={styles.item}>
      <div className={styles.itemHead}>
        <h2 className={styles.itemTitle}>
          <Link to={`/klienci/${item.client.id}`}>{item.client.name ?? item.client.phones[0] ?? 'Klient'}</Link>
        </h2>
        <Badge tone={label.overdue ? 'danger' : 'info'}>{label.text}</Badge>
      </div>
      <ul className={styles.lines}>
        <li className={styles.line}>
          <MapPin aria-hidden="true" />
          <span>
            {item.label ? `${item.label}: ` : ''}
            {item.address}
          </span>
        </li>
        <li className={styles.line}>
          <CalendarCheck aria-hidden="true" />
          <span>
            {item.serviceType?.name ?? 'Przegląd'} do {formatDateShort(item.due)}
          </span>
        </li>
        {item.lastVisitAt && (
          <li className={styles.line}>
            <History aria-hidden="true" />
            <span>Ostatnia wizyta: {formatDateShort(item.lastVisitAt)}</span>
          </li>
        )}
        {item.note && <li className={styles.muted}>{item.note}</li>}
      </ul>
      <div className={styles.itemActions}>
        {phone && (
          <ButtonAnchor href={telHref(phone)} variant="secondary" icon={<Phone aria-hidden="true" />}>
            Zadzwoń
          </ButtonAnchor>
        )}
        <ButtonLink to={`/wizyty/nowa?${params}`} variant="primary" icon={<CalendarPlus aria-hidden="true" />}>
          Umów wizytę
        </ButtonLink>
      </div>
    </li>
  );
}
